import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAllCategoryItems } from '@/hooks/use-items';
import { useT } from '@/i18n/use-t';
import { extractApiError } from '@/lib/api-error';

/**
 * RFL-206 — quick-add items from a group node's bound catalog category as
 * child item-nodes. Nothing is selected by default (not everyone wants
 * every child); the admin ticks the specific items, or uses "Select all"
 * to add the whole category. Adds run through the normal create-node
 * path, so the same sortOrder / cap / audit rules apply.
 *
 * RL-367: the list is paged to completion, so "Select all" really is the
 * whole category rather than the first API page.
 */
export function CategoryItemPicker({
  categoryId,
  onAdd,
}: {
  categoryId: string;
  onAdd: (items: { id: string; name: string }[]) => Promise<void>;
}) {
  const t = useT();
  const itemsQuery = useAllCategoryItems(categoryId);
  const items = useMemo(() => itemsQuery.data ?? [], [itemsQuery.data]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const allSelected = items.length > 0 && selected.size === items.length;

  function selectAll(): void {
    setSelected(new Set(items.map((i) => i.id)));
  }
  function clearAll(): void {
    setSelected(new Set());
  }

  async function add(): Promise<void> {
    setError(null);
    const chosen = items.filter((i) => selected.has(i.id)).map((i) => ({ id: i.id, name: i.name }));
    if (chosen.length === 0) return;
    setBusy(true);
    try {
      await onAdd(chosen);
      setSelected(new Set());
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-2 rounded-md border border-border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium">
          {t('hotkeys.picker.title', 'Add items from this category')}
        </span>
        <div className="flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={selectAll}
            disabled={items.length === 0 || allSelected}
          >
            {t('hotkeys.picker.selectAll', 'Select all')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearAll}
            disabled={selected.size === 0}
          >
            {t('hotkeys.picker.clear', 'Clear')}
          </Button>
        </div>
      </div>

      {itemsQuery.isLoading ? (
        <p className="text-xs text-muted-foreground">
          {t('hotkeys.picker.loading', 'Loading items…')}
        </p>
      ) : itemsQuery.isError ? (
        <p className="text-xs text-destructive">
          {t('hotkeys.picker.loadFailed', 'Failed to load the items in this category.')}
        </p>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {t('hotkeys.picker.empty', 'This category has no items.')}
        </p>
      ) : (
        <ul className="max-h-48 space-y-0.5 overflow-y-auto">
          {items.map((item) => (
            <li key={item.id}>
              <label className="flex items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-secondary">
                <input
                  type="checkbox"
                  checked={selected.has(item.id)}
                  onChange={() => toggle(item.id)}
                />
                <span className="truncate">{item.name}</span>
                <span className="ml-auto font-mono text-[10px] text-muted-foreground">
                  {item.sku}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}

      {error !== null && <p className="text-xs text-destructive">{error}</p>}

      <Button
        type="button"
        size="sm"
        onClick={() => void add()}
        disabled={busy || selected.size === 0}
      >
        {busy
          ? t('hotkeys.picker.adding', 'Adding…')
          : (selected.size === 1
              ? t('hotkeys.picker.addOne', 'Add {count} item')
              : t('hotkeys.picker.addMany', 'Add {count} items')
            ).replace('{count}', String(selected.size))}
      </Button>
    </div>
  );
}
